/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { hasGaps, overlaps } from '../../../../shared/utils/objectUtils';
import { all, integer, required, text } from '../../../../shared/validations/standardChecks';
import { specialDocumentTypes } from '../services/constants';
import { useAttachmentsConfig } from './useAttachmentsConfig';
import { isCombinedAttachmentPossible } from '../services/attachments.helper';

export function useAttachmentsValidations() {
  const { technicalAttachmentTypes, attachmentTypesConfig, attachmentsConfigReady } = useAttachmentsConfig();

  const checkedAttachmentTypes = values =>
    technicalAttachmentTypes
      ?.map(type => type.type)
      .filter(key => key !== specialDocumentTypes.COMBINED)
      .filter(key => Object.entries(values).find(([k, v]) => (k === `${key}from` || k === `${key}to`) && v !== false));

  const numberOfChecked = values =>
    technicalAttachmentTypes
      ?.map(type => type.type)
      .filter(key => key !== specialDocumentTypes.COMBINED)
      .filter(key => Object.entries(values).find(([k, v]) => (k === `${key}from` || k === `${key}to`) && v !== false))
      .length;

  const numberOfFilled = values =>
    technicalAttachmentTypes
      ?.map(type => type.type)
      .filter(key => key !== specialDocumentTypes.COMBINED)
      .filter(key => values[`${key}from`] && values[`${key}to`]).length;

  function getRangesFromScopes(values, scopes) {
    const ranges = [];
    scopes.forEach((attachmentType) => {
      const start = parseInt(values[`${attachmentType}from`]);
      const end = parseInt(values[`${attachmentType}to`]);
      if (start && end) ranges.push({ start, end });
    });

    return ranges;
  }

  async function doGapValidation({ form }) {
    const values = form.getValues();
    if (numberOfChecked(values) !== numberOfFilled(values)) return true;
    const checked = checkedAttachmentTypes(values);
    const ranges = getRangesFromScopes(values, checked);
    ranges.sort((a, b) => a.start - b.start);
    if (ranges.length > 1) return !hasGaps(ranges) || 'validation.service.page.accounted';
    return true;
  }

  const isCombinedDisabled = (persistedFiles, files) => !isCombinedAttachmentPossible(files, attachmentTypesConfig);

  async function doRangeValidation({ form, type }) {
    const values = form.getValues();
    const start = parseInt(values[`${type}from`]);
    const end = parseInt(values[`${type}to`]);
    if (start && end && start > end) {
      return 'validation.service.page.range';
    }
    return true;
  }

  async function doOverlapValidation({ form }) {
    const values = form.getValues();
    if (numberOfChecked(values) !== numberOfFilled(values)) return true;
    const checked = checkedAttachmentTypes(values);
    const ranges = getRangesFromScopes(values, checked);
    if (ranges.length > 1) return !overlaps(ranges) || 'validation.service.page.overlap';
    return true;
  }

  const optionalNumberValidation = () => ({
    validate: {
      all: all(),
      integer: integer(0, 999999),
    },
  });

  const requiredNumberValidation = fieldName => ({
    validate: {
      all: all(),
      required: required(fieldName),
      integer: integer(0, 999999),
    },
  });

  const documentTypeValidation = fieldName => () => ({
    validate: {
      all: all(),
      required: required(fieldName),
      string: text(1, 128),
    },
  });

  const figureToBePublishedValidation = () => ({
    validate: {
      all: all(),
      string: text(1, 4),
    },
  });

  const requiredValidation = fieldName => ({
    validate: {
      all: all(),
      required: required(fieldName),
    },
  });

  const pageNumberValidation = ({ form, type, fieldName }) => ({
    validate: {
      all: all(),
      required: required(fieldName),
      integer: integer(0, 999999),
      range: () => (fieldName === 'pagination.to.page' ? doRangeValidation({ form, type }) : true),
    },
  });

  const gapsValidation = ({ form, name }) => ({
    validate: {
      gaps: () => doGapValidation({ form, name }),
    },
  });

  const overlapValidation = ({ form, name }) => ({
    validate: {
      overlap: () => doOverlapValidation({ form, name }),
    },
  });

  return {
    optionalNumberValidation,
    requiredNumberValidation,
    documentTypeValidation,
    figureToBePublishedValidation,
    gapsValidation,
    overlapValidation,
    pageNumberValidation,
    isCombinedDisabled,
    attachmentsConfigReady,
    technicalAttachmentTypes,
    requiredValidation,
  };
}
